import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { PageLayout } from '../../layouts/PageLayout'
import { Button } from '../../components/ui/Button'
import {
  continueRemediationSession,
  fetchAssessmentTimeline,
  fetchComplianceOperationsWorkspace,
} from '../../services/complianceLifecycleApi'
import { buildRemediationJourneyUrl } from './journeyRemediationNav'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

type CycleRow = {
  sessionId: string
  standardCode: string
  assessmentCycle: number
  lifecycleStatus: string
  label: string
}

type QueueItem = {
  sessionId: string
  standardCode: string
  controlKey: string
  controlTitle?: string
  unresolvedStatus: string
  remediationRoute?: string
  questionId?: string | null
}

export default function ComplianceCenterOperationsWorkspace() {
  const { t } = useI18n()
  const { token } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cycles, setCycles] = useState<Array<{ standardCode: string; cycles: CycleRow[] }>>([])
  const [controlsWork, setControlsWork] = useState({ failed: 0, partial: 0, evidenceGaps: 0, total: 0 })
  const [queue, setQueue] = useState<QueueItem[]>([])
  const [timeline, setTimeline] = useState<Array<{ title: string; occurredAt: string; standardCode?: string }>>([])
  const [phases, setPhases] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const [ops, tl] = await Promise.all([
        fetchComplianceOperationsWorkspace(token),
        fetchAssessmentTimeline(token),
      ])
      setCycles(Array.isArray(ops?.assessmentCycles) ? ops.assessmentCycles : [])
      setControlsWork(ops?.controlsRequiringWork || { failed: 0, partial: 0, evidenceGaps: 0, total: 0 })
      setQueue(Array.isArray(ops?.remediationQueue) ? ops.remediationQueue : [])
      setPhases(ops?.currentPhaseByStandard || {})
      setTimeline(Array.isArray(tl?.events) ? tl.events : [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load operations workspace')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load])

  const onContinueRemediation = async (item: QueueItem) => {
    if (!token) return
    await continueRemediationSession(token, item.sessionId)
    window.location.href =
      item.remediationRoute ||
      buildRemediationJourneyUrl({
        sessionId: item.sessionId,
        standardCode: item.standardCode,
        controlKey: item.controlKey,
        mode: 'remediation',
      })
  }

  return (
    <PageLayout
      title={t('complianceCenterUi.operations.title', 'Compliance Operations')}
      subtitle={t(
        'complianceCenterUi.operations.subtitle',
        'Assessment cycles, remediation queue, and certification readiness across enabled standards.',
      )}
    >
      {loading ? <p>{t('common.loading', 'Loading…')}</p> : null}
      {error ? <p className="text-red-600">{error}</p> : null}

      <section className={`${listStyles.panel} space-y-3`}>
        <h3 className="text-base font-semibold">
          {t('complianceCenterUi.operations.cycles', 'Assessment cycles')}
        </h3>
        {cycles.length === 0 ? (
          <p className="text-sm text-slate-600">
            {t('complianceCenterUi.operations.noCycles', 'No assessment cycles yet.')}
          </p>
        ) : (
          cycles.map(({ standardCode, cycles: rows }) => (
            <div key={standardCode} className="rounded border border-slate-200 p-3">
              <p className="text-sm font-medium">
                {standardCode} — {t('complianceCenterUi.operations.currentPhase', 'Current phase')}:{' '}
                {phases[standardCode] || 'NOT_STARTED'}
              </p>
              <ul className="mt-2 space-y-1 text-sm">
                {rows.map((row) => (
                  <li key={row.sessionId} className="flex flex-wrap items-center gap-2">
                    <span>{row.label}</span>
                    <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{row.lifecycleStatus}</span>
                    <Link to={`/compliance-workflow?sessionId=${encodeURIComponent(row.sessionId)}`}>
                      {t('complianceCenterUi.operations.openSession', 'Open')}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </section>

      <section className={`${listStyles.panel} mt-4 space-y-2`}>
        <h3 className="text-base font-semibold">
          {t('complianceCenterUi.operations.controlsWork', 'Controls requiring work')}
        </h3>
        <p className="text-sm text-slate-700">
          {controlsWork.failed} {t('complianceCenterUi.operations.failed', 'failed')} ·{' '}
          {controlsWork.evidenceGaps} {t('complianceCenterUi.operations.evidenceGaps', 'evidence gaps')} ·{' '}
          {controlsWork.partial} {t('complianceCenterUi.operations.partial', 'partial')}
        </p>
      </section>

      <section className={`${listStyles.panel} mt-4 space-y-2`}>
        <h3 className="text-base font-semibold">
          {t('complianceCenterUi.operations.remediationQueue', 'Remediation queue')}
        </h3>
        {queue.length === 0 ? (
          <p className="text-sm text-slate-600">{t('complianceCenterUi.operations.queueEmpty', 'No open items.')}</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {queue.slice(0, 20).map((item) => (
              <li key={`${item.sessionId}-${item.controlKey}`} className="flex flex-wrap items-center gap-2">
                <span>
                  [{item.standardCode}] {item.controlTitle || item.controlKey} — {item.unresolvedStatus}
                </span>
                <Button type="button" size="small" color="outline" onClick={() => void onContinueRemediation(item)}>
                  {t('complianceCenterUi.operations.continueRemediation', 'Continue remediation')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={`${listStyles.panel} mt-4 space-y-2`}>
        <h3 className="text-base font-semibold">
          {t('complianceCenterUi.operations.timeline', 'Assessment timeline')}
        </h3>
        <ul className="space-y-2 text-sm">
          {timeline.slice(0, 12).map((ev) => (
            <li key={`${ev.title}-${ev.occurredAt}`}>
              <span className="text-slate-500">{new Date(ev.occurredAt).toLocaleDateString()}</span> — {ev.title}
              {ev.standardCode ? ` [${ev.standardCode}]` : ''}
            </li>
          ))}
        </ul>
      </section>
    </PageLayout>
  )
}
